// src/components/includes/EnrollmentFormPopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import styles from './EnrollmentFormPopup.module.css';
import { FaChevronLeft } from 'react-icons/fa';
import Captcha from './Captcha';
import { API } from "../../config";

const EnrollmentFormPopup = ({ isVisible, setIsVisible }) => {
    const [isIconVisible, setIsIconVisible] = useState(false);
    const [courses, setCourses] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        courseId: '',
        message: '',
    });
    const [captchaInput, setCaptchaInput] = useState('');
    const [captchaValue, setCaptchaValue] = useState('');
    
    // Create a ref to access the Captcha component's methods
    const captchaRef = useRef(null);

    // fetch courses
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await fetch(API.COURSES);
                const data = await response.json();
                setCourses(data);
            } catch (error) {
                console.error("Error fetching courses:", error);
            }
        };
        fetchCourses();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Validation for name (no numbers)
        if (name === 'name' && /\d/.test(value)) {
            return;
        }
        
        // Validation for phone (only numbers and max 10 characters)
        if (name === 'phone') {
            if (!/^\d*$/.test(value) || value.length > 10) {
                return;
            }
        }
        
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const resetFormAndCaptcha = () => {
        setFormData({ name: '', email: '', phone: '', courseId: '', message: '' });
        setCaptchaInput('');
        setCaptchaValue('');
        // Call the refresh function on the Captcha component if it exists
        if (captchaRef.current && typeof captchaRef.current.refreshCaptcha === 'function') {
            captchaRef.current.refreshCaptcha();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // **New Email Validation Check** 📧
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Please enter a valid email address (e.g., example@domain.com).');
            return; // Stop form submission
        }

        // Final check for phone number length
        if (formData.phone.length !== 10) {
            alert('Phone number must be exactly 10 digits long.');
            return;
        }

        if (captchaInput.toLowerCase() !== captchaValue.toLowerCase()) {
            alert('Incorrect CAPTCHA. Please try again.');
            return;
        }

        try {
            const response = await fetch(API.ENROLL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const result = await response.json();

            if (!response.ok) throw new Error(result.error || 'Something went wrong');

            alert(result.message);
            setIsVisible(false);
            setIsIconVisible(true);
            resetFormAndCaptcha();
        } catch (error) {
            console.error('Failed to submit form:', error);
            alert('Error submitting form: ' + error.message);
        }
    };

    const handleClose = () => {
        setIsVisible(false);
        setIsIconVisible(true);
        resetFormAndCaptcha();
    };

    const handleIconClick = () => {
        setIsIconVisible(false);
        setIsVisible(true);
        resetFormAndCaptcha();
    };

    return (
        <>
            <div className={`${styles.popupContainer} ${isVisible ? styles.visible : ''}`}>
                <div className={styles.popupContent}>
                    <button className={styles.closeButton} onClick={handleClose}>
                        &times;
                    </button>
                    <h3 className={styles.popupTitle}>Quick Enquiry</h3>
                    <form onSubmit={handleSubmit} className={styles.enrollmentForm}>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name" required />
                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Your Email" required />
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Your Phone Number" maxLength="10" required />
                        <select name="courseId" value={formData.courseId} onChange={handleChange} required>
                            <option value="" disabled>Select a Course</option>
                            {courses.map((course) => (
                                <option key={course.id} value={course.id}>{course.coursename}</option>
                            ))}
                        </select>

                        <div className={styles.captchaSection}>
                            <Captcha setCaptchaValue={setCaptchaValue} ref={captchaRef} />
                            <input
                                type="text"
                                name="captcha"
                                value={captchaInput}
                                onChange={(e) => setCaptchaInput(e.target.value)}
                                placeholder="Enter CAPTCHA"
                                required
                            />
                        </div>

                        <button type="submit" className={styles.submitButton}>Submit</button>
                    </form>
                </div>
            </div>

            {isIconVisible && (
                <button className={styles.toggleButton} onClick={handleIconClick}>
                    <FaChevronLeft className={styles.toggleIcon} />
                </button>
            )}
        </>
    );
};

export default EnrollmentFormPopup;